import { differenceInDays } from 'date-fns'

import {
	CHAINS_CONFIG,
	CHAINS_CONFIG_BY_SLUG,
	type ChainConfig,
} from '#lib/constants/chainsConfig.js'
import type {
	ChainId,
	ChainDecentralizationData,
	ChainData,
	ChainFinancialsData,
} from '#lib/models/index.js'

export const isChainPos = (chain: ChainData | ChainDecentralizationData) =>
	chain.consensus != null && chain.consensus !== 'pow'

export const isChainPow = (chain: ChainData | ChainDecentralizationData) =>
	chain.consensus != null && chain.consensus === 'pow'

export const getToken = (
	chain: Nullable<ChainData | ChainFinancialsData | ChainDecentralizationData | ChainConfig>,
	type: 'price' | 'stake' | 'gas' = 'price'
) => {
	if (chain == null || !chain.tokens) return null

	if (typeof chain.tokens === 'string') return chain.tokens

	switch (type) {
		case 'price':
			return chain.tokens.priceToken ?? null
		case 'stake':
			return chain.tokens.stakeToken ?? null
		case 'gas':
			return chain.tokens.gasToken ?? null
		default:
			return null
	}
}

const CONSENSUS_SIMILARITY_MAPPING: Record<string, number> = {
	pow: 1,
	dpos: 0,
	pos: 0,
	npos: 0,
	ppos: 0,
	por: 0,
	pouw: 0,
	scp: 0,
	op: 0,
	zk: 0,
	posa: 0,
}

export const isConsensusSimilar = (left: Nullable<string>, right: Nullable<string>) =>
	left == null || right == null
		? left === right
		: CONSENSUS_SIMILARITY_MAPPING[left] === CONSENSUS_SIMILARITY_MAPPING[right]

export const isChainNew = (chain: Pick<ChainConfig, 'listed'>) =>
	chain.listed ? differenceInDays(new Date(), chain.listed) < 90 : false

/**
 * "Cronos", not "Cronos Network". Keeps words that tell chains apart:
 * "Keeta Mainnet" vs "Keeta Testnet", "Tezos" vs "Tezos Ecosystem"
 */
export const getShortChainName = (name: string) =>
	name.replace(/\b(blockchain|network)\b/gi, '').trim()

export const getChainOptions = <T extends Pick<ChainConfig, 'id' | 'name' | 'slug'>>(chains: T[]) =>
	chains
		.map(({ name, id, slug }) => ({
			label: getShortChainName(name),
			value: id,
			slug,
		}))
		.toSorted((a, b) => a.label.localeCompare(b.label))

/** Chain ids for the `?chains=` URL param (slugs), skipping unknown and unlisted slugs */
export const getChainIdsFromSlugs = (slugs: string[]) =>
	slugs
		.filter(slug => CHAINS_CONFIG_BY_SLUG[slug]?.listed)
		.map(slug => CHAINS_CONFIG_BY_SLUG[slug].id)

export const getSlugsFromChainIds = (ids: ChainId[]) =>
	ids
		.filter(id => CHAINS_CONFIG[id])
		.map(id => CHAINS_CONFIG[id].slug)
		.toSorted((a, b) => a.localeCompare(b))
